import { PropsWithChildren } from 'react';
import { Navigate } from 'react-router';
import { useUserContext } from '../../hooks/userContext';
import { useDesignerApplicationContext } from '../../hooks/designer-application-context';
import { Pages, isAdminUser } from '../../utils';
import { CircularProgress, Box } from '@mui/material';
import { DesignerApplicationSteps } from '../../__generated__/types';
import { usePaymentSettings } from '../../contexts/PaymentSettingsContext';

type OnboardingStatus = 
  | 'pending_application'  // Can access application form
  | 'application_submitted' // Application submitted, under review
  | 'application_approved'  // Approved, needs payment
  | 'membership_active';    // Paid, can access all features

interface OnboardingStatusGuardProps extends PropsWithChildren {
  requiresStatus: OnboardingStatus;
  redirectTo?: string;
}

/**
 * OnboardingStatusGuard enforces progressive designer onboarding.
 * Blocks access to features until designer reaches required onboarding milestone.
 */
export const OnboardingStatusGuard: React.FC<OnboardingStatusGuardProps> = ({ 
  children, 
  requiresStatus,
  redirectTo = Pages.DASHBOARD 
}) => {
  const { user, loading } = useUserContext();
  const { application } = useDesignerApplicationContext();
  const { isPaymentEnabled, isTestMode } = usePaymentSettings();


  // Show loading while checking user status
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  // If no user, redirect to login
  if (!user) {
    return <Navigate to={Pages.LOGIN} replace />;
  }

  // Admins bypass onboarding checks
  if (isAdminUser(user)) {
    return <>{children}</>;
  }

  // Only apply to designers
  if (user.accountType !== 'Designer') {
    return <>{children}</>;
  }

  const userOnboardingStatus = user.onboardingStatus;
  const applicationStatus = application?.status;
  const isBetaMode = !isPaymentEnabled() || isTestMode();

  // Determine current onboarding level
  const getCurrentOnboardingLevel = (): OnboardingStatus | null => {
    // In beta mode, designers with pending_profile_completion can access application
    if (isBetaMode && (userOnboardingStatus === 'pending_profile_completion' || userOnboardingStatus === 'profile_completed')) {
      return 'pending_application';
    }

    // Check application status
    if (!applicationStatus || applicationStatus === DesignerApplicationSteps.CompleteApplication) {
      return 'pending_application';
    }

    if (applicationStatus === DesignerApplicationSteps.UnderReview) {
      return 'application_submitted';
    }

    if (applicationStatus === DesignerApplicationSteps.Approved) {
      return 'application_approved';
    }

    if (applicationStatus === DesignerApplicationSteps.MembershipActive || userOnboardingStatus === 'active') {
      return 'membership_active';
    }

    return null;
  };

  const currentLevel = getCurrentOnboardingLevel();

  // Define progression order
  const progressionOrder: OnboardingStatus[] = [
    'pending_application',
    'application_submitted',
    'application_approved',
    'membership_active'
  ];

  const currentIndex = currentLevel ? progressionOrder.indexOf(currentLevel) : -1;
  const requiredIndex = progressionOrder.indexOf(requiresStatus);


  // Check if user has reached required level
  if (currentIndex < requiredIndex) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};